import { useEffect, useState } from "react";

import { buildTaskThreadIndex } from "~/lib/taskThreadLink";
import { ChatTaskBoardPane, type ChatTaskBoardLayout } from "./ChatTaskBoardPane";
import { ChatTaskDetailPane } from "./ChatTaskDetailPane";
import { TaskWorkSessionDialog } from "./TaskWorkSessionDialog";
import { CHAT_TASK_PANEL_REFRESH_MS, type ChatTaskPanelContext } from "./useChatTaskPanelContext";
import { useChatTaskWorkSession } from "./useChatTaskWorkSession";
import { flattenTaskTree, type ProjectTaskViewProps } from "./taskViewTypes";
import { useProjectTaskBoard } from "./useProjectTaskBoard";
import { useProjectTaskWorkbenchData } from "./useProjectTaskWorkbenchData";

export interface ChatTaskPanelBodyProps {
  /** Non-null: the caller gates on a resolved project before mounting this. */
  readonly projectId: string;
  readonly context: ChatTaskPanelContext;
}

/**
 * Orchestrator for the chat task panel: owns every shared read, the task
 * mutations, and the selected task, and hands plain props to whichever pane
 * applies.
 *
 * Mounted only once a project id is known, because the board and workbench
 * hooks fetch on mount and cannot be called conditionally. The caller keys this
 * component by project and anchored task, so a thread switch remounts it rather
 * than needing an effect to resync {@link selectedTaskId}.
 *
 * Writes go through the same `useProjectTaskBoard` handlers the project surface
 * uses, so optimistic updates, rollback and error reporting behave identically
 * on both surfaces rather than diverging into a second mutation path.
 */
export function ChatTaskPanelBody(props: ChatTaskPanelBodyProps) {
  const { context } = props;
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(context.taskId);
  const [layout, setLayout] = useState<ChatTaskBoardLayout>("board");
  const board = useProjectTaskBoard(props.projectId, context.members, context.agentMembers);
  const workbench = useProjectTaskWorkbenchData(props.projectId, selectedTaskId);
  const workSession = useChatTaskWorkSession({
    projectId: props.projectId,
    project: context.project,
    forgeTarget: context.forgeTarget,
    tasks: board.tasks,
    onSessionStarted: () => {
      void board.reload();
      void workbench.reload();
    },
  });

  useTaskPanelRefresh(board.reload, workbench.reload);

  const allTasks = flattenTaskTree(board.tasks);
  const task =
    selectedTaskId === null
      ? null
      : (allTasks.find((candidate) => candidate.id === selectedTaskId) ?? null);

  if (task !== null) {
    return (
      <>
        <ChatTaskDetailPane
          task={task}
          projectTitle={context.projectTitle}
          projectIcon={context.projectIcon}
          projectIconUrl={context.projectIconUrl}
          allTasks={allTasks}
          members={context.members}
          agentMembers={context.agentMembers}
          agentMembersLoading={context.agentMembersLoading}
          agentMembersError={context.agentMembersError}
          board={board}
          workbenchData={workbench}
          threadSummaries={workSession.threadSummaries}
          onStartWork={workSession.launcher.openForTask}
          onOpenThread={workSession.openThread}
          onOpenTask={setSelectedTaskId}
          /* Only an unanchored thread arrives here from the board, and only that
             case has a board to go back to. An anchored thread opens on its own
             task, so a back control there would point at nothing. */
          onBack={context.taskId === null ? () => setSelectedTaskId(null) : null}
        />
        <TaskWorkSessionDialog launcher={workSession.launcher} />
      </>
    );
  }

  const viewProps: ProjectTaskViewProps = {
    tasks: board.tasks,
    threadIndex: buildTaskThreadIndex(workSession.threadSummaries),
    pendingTaskIds: board.pendingTaskIds,
    onOpenTask: setSelectedTaskId,
    onSetStatus: board.setStatus,
    workflow: board.workflow,
    mutationDisabled: board.workflowMutationBlocked,
  };

  return (
    <ChatTaskBoardPane
      layout={layout}
      onLayoutChange={setLayout}
      board={board}
      viewProps={viewProps}
      projectTitle={context.projectTitle}
    />
  );
}

/**
 * Keep the panel current while an agent works the task.
 *
 * CMMD exposes no push channel for task mutations, so the surface polls. The
 * interval skips hidden documents: a backgrounded tab cannot be watched, and
 * polling it only produces requests nobody reads.
 */
function useTaskPanelRefresh(
  reloadBoard: () => Promise<void> | void,
  reloadWorkbench: () => Promise<void> | void,
): void {
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void reloadBoard();
      void reloadWorkbench();
    }, CHAT_TASK_PANEL_REFRESH_MS);
    return () => clearInterval(timer);
  }, [reloadBoard, reloadWorkbench]);
}
